(ns io.daskul.webapp.hooks.aggregates
  "Reusable React hooks for aggregate operations.

   ## Available Hooks

   - `use-aggregate` - Simple hook for loading a single aggregate by ID
   - `use-current-aggregate` - Hook for accessing the current aggregate
   - `use-aggregate-list` - Fetch and manage list of aggregates
   - `use-aggregate-record` - Single record fetch and state
   - `use-aggregate-form` - Form state management for create/edit
   - `use-fsm` - FSM state and transitions
   - `use-aggregate-delete` - Delete with confirmation state"
  (:require
   [uix.core :as uix]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.widgets.fsm.transition-logic :as transition-logic]))

;; ============================================================================
;; use-aggregate (simple - loads and returns single aggregate)
;; ============================================================================

(defn use-aggregate
  "Hook for loading and accessing a single aggregate.

   Returns {:data aggregate-map
            :loading? boolean
            :error error-map}"
  [agg-key id]
  (let [data @(rf/subscribe [::agg.subs/by-id agg-key id])
        loading? @(rf/subscribe [::agg.subs/loading? agg-key])
        error @(rf/subscribe [::agg.subs/error agg-key])]

    (uix/use-effect
     (fn []
       (when id
         (rf/dispatch [::agg.events/get agg-key id]))
       js/undefined)
     [agg-key id])

    {:data data
     :loading? loading?
     :error error}))

(defn use-current-aggregate
  "Hook for accessing the current aggregate (from db/current-path).

   Returns {:data aggregate-map
            :loading? boolean
            :error error-map}"
  [agg-key]
  (let [data @(rf/subscribe [::agg.subs/current agg-key])
        loading? @(rf/subscribe [::agg.subs/loading? agg-key])
        error @(rf/subscribe [::agg.subs/error agg-key])]

    {:data data
     :loading? loading?
     :error error}))

;; ============================================================================
;; use-aggregate-list
;; ============================================================================

(defn use-aggregate-list
  "Hook for fetching and managing a list of aggregates.

   Options:
     :aggregate - Aggregate keyword (e.g., :program, :student)
     :auto-fetch? - Auto-fetch on mount (default: true)

   Returns map with:
     :items - Vector of aggregate records
     :loading? - Boolean loading state
     :error - Error object or nil
     :refresh! - Function to manually refresh the list"
  [{:keys [aggregate auto-fetch?]
    :or {auto-fetch? true}}]
  (let [items (urf/use-subscribe [::agg.subs/all aggregate])
        loading? (urf/use-subscribe [::agg.subs/loading? aggregate])
        error (urf/use-subscribe [::agg.subs/error aggregate])

        refresh! (uix/use-callback
                  (fn []
                    (rf/dispatch [::agg.events/list aggregate]))
                  [aggregate])]

    ;; Auto-fetch on mount
    (uix/use-effect
     (fn []
       (when auto-fetch?
         (rf/dispatch [::agg.events/list aggregate]))
       js/undefined)
     [aggregate auto-fetch?])

    {:items items
     :loading? loading?
     :error error
     :refresh! refresh!}))

;; ============================================================================
;; use-aggregate-record
;; ============================================================================

(defn use-aggregate-record
  "Hook for fetching and managing a single aggregate record.

   Options:
     :aggregate - Aggregate keyword (e.g., :program, :student)
     :id - Record ID to fetch
     :auto-fetch? - Auto-fetch on mount (default: true)

   Returns map with:
     :record - The aggregate record or nil
     :loading? - Boolean loading state
     :error - Error object or nil
     :refresh! - Function to manually refresh"
  [{:keys [aggregate id auto-fetch?]
    :or {auto-fetch? true}}]
  (let [record (urf/use-subscribe [::agg.subs/current aggregate])
        loading? (urf/use-subscribe [::agg.subs/loading? aggregate])
        error (urf/use-subscribe [::agg.subs/error aggregate])

        refresh! (uix/use-callback
                  (fn []
                    (when id
                      (rf/dispatch [::agg.events/get aggregate id])))
                  [aggregate id])]

    ;; Auto-fetch on mount or when ID changes
    (uix/use-effect
     (fn []
       (when (and auto-fetch? id)
         (rf/dispatch [::agg.events/get aggregate id]))
       js/undefined)
     [aggregate id auto-fetch?])

    {:record record
     :loading? loading?
     :error error
     :refresh! refresh!}))

;; ============================================================================
;; use-aggregate-form
;; ============================================================================

(defn use-aggregate-form
  "Hook for managing aggregate form state (create or edit).

   Options:
     :aggregate - Aggregate keyword (e.g., :program, :student)
     :mode - :create or :edit
     :id - Record ID (required for :edit mode)
     :initial-data - Initial form data (optional)
     :on-success - Callback on successful save (optional)
     :fetch-record? - Auto-fetch record in edit mode (default: true)
     :fetch-schema? - Auto-fetch schema (default: true)

   Returns map with:
     :form-data - Current form data map
     :set-field! - Function (field value) to update a field
     :set-form-data! - Function to replace entire form data
     :submit! - Function (event) to submit the form
     :reset! - Function to reset form to initial/record data
     :saving? - Boolean saving state
     :errors - Field errors map
     :clear-field-error! - Function (field) to clear a specific error
     :dirty? - Boolean whether form has unsaved changes
     :schema - Schema fields (if fetch-schema? is true)
     :schema-loading? - Schema loading state"
  [{:keys [aggregate mode id initial-data on-success fetch-record? fetch-schema?]
    :or {fetch-record? true
         fetch-schema? true}}]
  (let [;; Subscriptions — use by-id (not ::current) to avoid stale current-id
        record (urf/use-subscribe [::agg.subs/by-id aggregate id])
        creating? (urf/use-subscribe [::agg.subs/creating? aggregate])
        updating? (urf/use-subscribe [::agg.subs/updating? aggregate])
        error (urf/use-subscribe [::agg.subs/error aggregate])
        schema (urf/use-subscribe [::agg.subs/schema aggregate (if (= mode :edit) :patch :create)])
        schema-loading? (urf/use-subscribe [::agg.subs/schema-loading? aggregate])

        ;; Local form state
        [form-data set-form-data-internal!] (uix/use-state (or initial-data {}))
        [original-data set-original-data!] (uix/use-state nil)

        saving? (if (= mode :edit) updating? creating?)

        ;; Derived: dirty check
        dirty? (and original-data (not= form-data original-data))

        ;; Set a single field
        set-field! (uix/use-callback
                    (fn [field value]
                      (set-form-data-internal! #(assoc % field value)))
                    [])

        ;; Replace entire form data
        set-form-data! (uix/use-callback
                        (fn [data]
                          (set-form-data-internal! data))
                        [])

        ;; Clear a specific field error
        clear-field-error! (uix/use-callback
                            (fn [field]
                              (rf/dispatch [::agg.events/clear-field-error aggregate field]))
                            [aggregate])

        ;; Reset form to original/initial data
        reset! (uix/use-callback
                (fn []
                  (set-form-data-internal! (or original-data initial-data {})))
                [original-data initial-data])

        ;; Submit handler
        submit! (uix/use-callback
                 (fn [e]
                   (when e (.preventDefault e))
                   (if (= mode :edit)
                     ;; For edit: only send editable fields (exclude readonly protected fields)
                     (let [schema-keys (->> (:fields schema)
                                            (remove :readonly)
                                            (map :name)
                                            set)
                           patch-data (select-keys form-data schema-keys)]
                       (rf/dispatch [::agg.events/patch aggregate id patch-data on-success]))
                     ;; For create: send all form data
                     (rf/dispatch [::agg.events/create aggregate form-data on-success])))
                 [aggregate mode id form-data schema on-success])]

    ;; Reset form state when entity ID changes (e.g., create → edit redirect)
    (uix/use-effect
     (fn []
       (set-original-data! nil)
       (set-form-data-internal! (or initial-data {}))
       js/undefined)
     [initial-data id])

    ;; Fetch record in edit mode
    (uix/use-effect
     (fn []
       (when (and fetch-record? (= mode :edit) id)
         (rf/dispatch [::agg.events/get aggregate id]))
       js/undefined)
     [aggregate mode id fetch-record?])

    ;; Fetch schema
    (uix/use-effect
     (fn []
       (when fetch-schema?
         (rf/dispatch [::agg.events/fetch-schema aggregate (if (= mode :edit) :patch :create)]))
       js/undefined)
     [aggregate mode fetch-schema?])

    ;; Initialize form data from schema defaults (create mode).
    ;; Merges initial-data on top of schema defaults so injected values take precedence.
    (uix/use-effect
     (fn []
       (when (and (= mode :create) schema (nil? original-data))
         (let [defaults (->> (:fields schema)
                             (keep (fn [field]
                                     (when (some? (:default field))
                                       [(:name field) (:default field)])))
                             (into {}))
               merged (merge defaults initial-data)]
           (when (seq merged)
             (set-original-data! merged)
             (set-form-data-internal! merged))))
       js/undefined)
     [mode schema original-data initial-data])

    ;; Re-sync form when record changes on server (e.g., after save, FSM transition, or re-fetch).
    ;; Resets original-data so the init effect below picks up fresh values.
    ;; Detects changes via :state or :updated-at divergence.
    (uix/use-effect
     (fn []
       (when (and (= mode :edit) record original-data)
         (let [record-state (:state record)
               form-state (:state original-data)
               state-changed? (and record-state form-state (not= record-state form-state))
               record-updated (:updated-at record)
               original-updated (:updated-at original-data)
               updated-changed? (and record-updated original-updated (not= record-updated original-updated))]
           (when (or state-changed? updated-changed?)
             (set-original-data! nil))))
       js/undefined)
     [mode record original-data])

    ;; Initialize form data from record (edit mode)
    ;; Supports cross-aggregate fields via :source-path in schema.
    ;; Always includes :state so the resync effect above can detect FSM transitions
    ;; (state is :field/protected so it's not in the patch schema fields).
    (uix/use-effect
     (fn []
       (when (and (= mode :edit) record schema (nil? original-data))
         (let [data (->> (:fields schema)
                         (map (fn [field]
                                (let [field-name (:name field)
                                      source-path (:source-path field)]
                                  [field-name
                                   (if source-path
                                     (get-in record source-path)
                                     (get record field-name))])))
                         (into {}))
               ;; Include :state and :updated-at for resync detection (even if not in schema)
               data (cond-> data
                      (:state record) (assoc :state (:state record))
                      (:updated-at record) (assoc :updated-at (:updated-at record)))]
           (set-original-data! data)
           (set-form-data-internal! data)))
       js/undefined)
     [mode record schema original-data])

    {:form-data form-data
     :set-field! set-field!
     :set-form-data! set-form-data!
     :submit! submit!
     :reset! reset!
     :saving? saving?
     :errors (:field-errors error)
     :error error
     :clear-field-error! clear-field-error!
     :dirty? dirty?
     :schema schema
     :schema-loading? schema-loading?}))

;; ============================================================================
;; use-fsm
;; ============================================================================

(defn use-fsm
  "Hook for managing FSM state and transitions.

   Options:
     :aggregate - Aggregate keyword (e.g., :program, :student)
     :id - Record ID

   Returns map with:
     :state - Current state string
     :state-ui - State UI metadata (label, icon, variant, description, alerts)
     :flags - State-based feature flags
     :edit - Edit config ({:readonly true} when form should be locked, nil otherwise)
     :children - Child operation restrictions (e.g., {:payment {:create false}})
     :transitions - Vector of available transitions with UI metadata
     :transition! - Function (event params? opts?) to trigger transition.
                    opts {:on-success (fn [record]) :on-failure (fn [errors])}
                    fire after the transition settles (threaded to
                    ::transition) — for UI-local reactions the framework can't
                    infer. on-success receives the transitioned record; the
                    record in app-db is re-fetched automatically regardless.
     :transitioning? - Boolean transitioning state
     :transition-event - Currently processing event name"
  [{:keys [aggregate id]}]
  (let [record (urf/use-subscribe [::agg.subs/by-id aggregate id])
        transitioning? (urf/use-subscribe [::agg.subs/transitioning? aggregate])
        transition-event (urf/use-subscribe [::agg.subs/transition-event aggregate])

        ;; FSM view data is under [:_meta :fsm]
        fsm-view (get-in record [:_meta :fsm])

        transition! (uix/use-callback
                     (fn [event & [params opts]]
                       (rf/dispatch [::agg.events/transition aggregate id event params opts]))
                     [aggregate id])]

    {:state (:state record)
     :state-ui (:state fsm-view)
     :flags (:flags fsm-view)
     :edit (:edit fsm-view)
     :children (:children fsm-view)
     ;; SMS-122: filter at the source so no renderer sees a :ui {:hidden true}
     ;; transition; primary is chosen by :ui :variant; unlabeled events humanize.
     :transitions (transition-logic/prepare-transitions (:transitions fsm-view))
     :transition! transition!
     :transitioning? transitioning?
     :transition-event transition-event}))

;; ============================================================================
;; use-aggregate-delete
;; ============================================================================

(defn use-aggregate-delete
  "Hook for delete operations with confirmation state.

   Options:
     :aggregate - Aggregate keyword
     :id - Record ID
     :on-success - Callback after successful delete

   Returns map with:
     :delete! - Function to execute delete
     :deleting? - Boolean deleting state
     :confirm-open? - Boolean modal state
     :open-confirm! - Function to open confirmation modal
     :close-confirm! - Function to close confirmation modal"
  [{:keys [aggregate id on-success]}]
  (let [deleting? (urf/use-subscribe [::agg.subs/deleting? aggregate])
        [confirm-open? set-confirm-open!] (uix/use-state false)

        open-confirm! (uix/use-callback #(set-confirm-open! true) [])
        close-confirm! (uix/use-callback #(set-confirm-open! false) [])

        delete! (uix/use-callback
                 (fn []
                   (rf/dispatch [::agg.events/delete aggregate id on-success])
                   (set-confirm-open! false))
                 [aggregate id on-success])]

    {:delete! delete!
     :deleting? deleting?
     :confirm-open? confirm-open?
     :open-confirm! open-confirm!
     :close-confirm! close-confirm!}))
